/* @jsxRuntime classic */
/** @jsx createElement */
import {
  BodyThree,
  BodyTwo,
  createElement,
  HeadlineFour,
  HeadlineThree,
  HeadlineTwo,
  OverlineFive,
  OverlineOne,
  OverlineThree,
} from '@nucleus/ncu-section-builder';
import { Height, InfoButtonsLayout, InfoButtonsWidth, InfoMaxWidth, SectionTypeEnum } from '@nucleus/types/web';
import { Alignment } from '@nucleus/types/web/alignment';
import { Position } from '@nucleus/types/web';
import { SectionDefaultBuilder } from '../SectionDefaultBuilder';
import {
  StandardBlockButtons,
  StandardSectionBackground,
  buildPlaceholderAction,
  buildPlaceholderMedia,
  buildStandardWrapper,
  listMediaOptionsInlineDefault,
} from '../helpers';
import { defaultText } from '../text';

const builder = new SectionDefaultBuilder<SectionTypeEnum.List>()
  .setWrapper(buildStandardWrapper<SectionTypeEnum.List>)
  .addLayout('layout1', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-1',
    height: Height.Medium,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        byline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [buildPlaceholderAction()],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemBodyAlignment: Alignment.Left,
        itemButtonAlignment: Alignment.Left,
        itemButtonsLayout: InfoButtonsLayout.Horizontal,
        itemButtonsWidth: InfoButtonsWidth.Auto,
        itemEnabledInfo: {
          overline: false,
          headline: true,
          byline: false,
          body: true,
          buttons: true,
          mediaItems: false,
        },
        itemHeadlineAlignment: Alignment.Left,
        itemMediaOptions: listMediaOptionsInlineDefault(lookConfig),
        listType: 'accordion',
      },
    ],
  }))
  .addLayout('layout2', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Bottom,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        headline: <HeadlineTwo>{defaultText.headline.short}</HeadlineTwo>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [buildPlaceholderAction()],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemBodyAlignment: Alignment.Left,
        itemButtonAlignment: Alignment.Left,
        itemButtonsLayout: InfoButtonsLayout.Horizontal,
        itemButtonsWidth: InfoButtonsWidth.Auto,
        itemEnabledInfo: {
          overline: false,
          headline: true,
          byline: false,
          body: true,
          buttons: true,
          mediaItems: true,
        },
        itemHeadlineAlignment: Alignment.Left,
        itemMediaOptions: listMediaOptionsInlineDefault(lookConfig),
        listType: 'stacked',
      },
    ],
  }))
  .addLayout('layout3', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-3',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Small,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [buildPlaceholderAction()],
        mediaItems: [
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
        ],
        itemBodyAlignment: Alignment.Left,
        itemButtonAlignment: Alignment.Left,
        itemButtonsLayout: InfoButtonsLayout.Horizontal,
        itemButtonsWidth: InfoButtonsWidth.Auto,
        itemEnabledInfo: {
          overline: false,
          headline: true,
          byline: false,
          body: true,
          buttons: true,
          mediaItems: false,
        },
        itemHeadlineAlignment: Alignment.Left,
        itemMediaOptions: listMediaOptionsInlineDefault(lookConfig),
        listType: 'stacked',
      },
    ],
  }))
  .addLayout('layout4', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Bottom,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockBodyMaxColumns: 2,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: [
          ...BodyTwo({ children: defaultText.body.long }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long2 }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long3 }),
        ],
        actions: [buildPlaceholderAction()],
        mediaItems: [],
        itemBodyAlignment: Alignment.Left,
        itemButtonAlignment: Alignment.Left,
        itemButtonsLayout: InfoButtonsLayout.Horizontal,
        itemButtonsWidth: InfoButtonsWidth.Auto,
        itemEnabledInfo: {
          overline: false,
          headline: true,
          byline: false,
          body: true,
          buttons: true,
          mediaItems: false,
        },
        itemHeadlineAlignment: Alignment.Left,
        itemMediaOptions: listMediaOptionsInlineDefault(lookConfig),
        listType: 'accordion',
      },
    ],
  }))
  .addLayout('layout5', ({ lookConfig }) => ({
    backgroundMedia: { presetMediaKey: 'sectionBackground', presetMediaOrder: 'first' },
    blockBackgroundMedia: {
      useImage: true,
      image: lookConfig?.placeholders?.LightBokeh,
      blending: { opacity: 0.15, blendMode: 'none', fade: 'none' },
      presetMediaKey: 'blockBackground',
      presetMediaOrder: 'first',
    },
    colorPaletteVariation: 'variation-2',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Bottom,
    blockHeadlineAlignment: Alignment.Right,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineOne>{defaultText.overline.standard}</OverlineOne>,
        headline: <HeadlineThree>{defaultText.headline.long}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.short}</BodyTwo>,
        actions: [buildPlaceholderAction()],
        mediaItems: [],
        itemBodyAlignment: Alignment.Left,
        itemButtonAlignment: Alignment.Left,
        itemButtonsLayout: InfoButtonsLayout.Horizontal,
        itemButtonsWidth: InfoButtonsWidth.Auto,
        itemEnabledInfo: {
          overline: false,
          headline: true,
          byline: false,
          body: true,
          buttons: true,
          mediaItems: false,
        },
        itemHeadlineAlignment: Alignment.Left,
        itemMediaOptions: listMediaOptionsInlineDefault(lookConfig),
        listType: 'accordion',
      },
    ],
  }))
  .addLayout('layout6', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Large,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blockButtonsWidth: InfoButtonsWidth.Stretch,
    blocks: [
      {
        overline: <OverlineFive>{defaultText.overline.standard}</OverlineFive>,
        headline: <HeadlineFour>{defaultText.headline.short}</HeadlineFour>,
        body: <BodyThree>{defaultText.body.standard}</BodyThree>,
        actions: [buildPlaceholderAction()],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemBodyAlignment: Alignment.Left,
        itemButtonAlignment: Alignment.Left,
        itemButtonsLayout: InfoButtonsLayout.Horizontal,
        itemButtonsWidth: InfoButtonsWidth.Auto,
        itemEnabledInfo: { overline: true, headline: true, byline: true, body: true, buttons: true, mediaItems: true },
        itemHeadlineAlignment: Alignment.Left,
        itemMediaOptions: listMediaOptionsInlineDefault(lookConfig),
        listType: 'stacked',
      },
      {
        overline: <OverlineFive>{defaultText.overline.standard}</OverlineFive>,
        headline: <HeadlineFour>{defaultText.headline.short2}</HeadlineFour>,
        body: <BodyThree>{defaultText.body.standard}</BodyThree>,
        actions: [buildPlaceholderAction()],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemBodyAlignment: Alignment.Left,
        itemButtonAlignment: Alignment.Left,
        itemButtonsLayout: InfoButtonsLayout.Horizontal,
        itemButtonsWidth: InfoButtonsWidth.Auto,
        itemEnabledInfo: {
          overline: false,
          headline: true,
          byline: false,
          body: true,
          buttons: true,
          mediaItems: true,
        },
        itemHeadlineAlignment: Alignment.Left,
        itemMediaOptions: listMediaOptionsInlineDefault(lookConfig),
        listType: 'stacked',
      },
    ],
  }));

export default builder.build();
